import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { ButtonLoading } from "../button-loading";
import { Button } from "../ui/button";
import { DialogFooter, DialogHeader } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export const DialogAdminUserInvitation = ({
  open,
  onClose = () => {},
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const createNewUser = async () => {
    if (userEmail === "") {
      toastError();
      return;
    }

    setLoading(true);
    try {
      await api.post<void>(`/admin/user/invite`, {
        email: userEmail,
      });

      toast({
        variant: "default",
        title: t("admin.user.invitation.toast.success.title"),
        description: t("admin.user.invitation.toast.success.description"),
      });

      setUserEmail("");
      onClose();
    } catch (error) {
      toastError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="w-full max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t("admin.user.invitation.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="userEmail">
            {t("admin.user.invitation.fields.userEmail.label")}
          </Label>
          <Input
            id="userEmail"
            placeholder={t(
              "admin.user.invitation.fields.userEmail.placeholder"
            )}
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            className={cn(isMobile ? "mt-2" : "")}
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t("admin.user.invitation.actions.cancel")}
          </Button>
          <ButtonLoading onClick={createNewUser} isLoading={loading}>
            {t("admin.user.invitation.actions.invite")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
